import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import api from '../../../../Component/axios_Interceptor/api';
import { SERVER_ADDRESS } from '../../../../Component/server';
import { AdminActionButton } from '../../../../Component/UI/AdminPageHeader';
import { createPortal } from 'react-dom';
import ModalCloseButton from '../../../../Component/UI/ModalCloseButton';

type Receipt = { order_seq: number; order_nickname: string; order_name: string; paid_amount: number;
  issued: boolean | number; receipt_number: string | null; issued_at: string | null };
const message = (error: any) => typeof error?.response?.data?.message === 'string'
  ? error.response.data.message : '현금영수증 기록을 저장하지 못했습니다. 다시 시도해주세요.';

export default function BankCashReceiptList({ liveSeq, onClose }: { liveSeq: number; onClose?: () => void }) {
  const [rows, setRows] = useState<Receipt[]>([]), [numbers, setNumbers] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(true), [saving, setSaving] = useState<number | null>(null), [error, setError] = useState('');
  const [searchDraft, setSearchDraft] = useState(''), [search, setSearch] = useState('');
  const [page, setPage] = useState(1), [pageSize, setPageSize] = useState(20), [total, setTotal] = useState(0);
  const [, renderQueue] = useState(0);
  const revision = useRef(0), pending = useRef(new Map<number, string>()), failed = useRef(new Map<number, string>());
  const saved = useRef(new Map<number, string>()), drafts = useRef(new Map<number, string>());
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null), running = useRef<Promise<boolean> | null>(null);
  const inFlight = useRef<number | null>(null), closing = useRef(false), closeRequest = useRef<() => void>(() => {});
  const dialog = useRef<HTMLDivElement>(null), closeButton = useRef<HTMLButtonElement>(null);
  const unsaved = pending.current.size > 0 || failed.current.size > 0 || saving !== null;
  const repaint = () => renderQueue(value => value + 1);

  const flush = useCallback((): Promise<boolean> => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    if (running.current) return running.current;
    if (!pending.current.size) return Promise.resolve(failed.current.size === 0);
    const generation = revision.current;
    const work = (async () => {
      try {
        while (pending.current.size && generation === revision.current) {
          const [seq, value] = pending.current.entries().next().value as [number, string];
          pending.current.delete(seq); failed.current.delete(seq); inFlight.current = seq; setSaving(seq); repaint();
          try {
            const { data } = await api.patch(`${SERVER_ADDRESS}orders/bank-cash-receipts`, { order_seq: seq, receipt_number: value });
            if (generation !== revision.current) return false;
            if (data?.order_seq !== seq || data.issued !== (value.length > 0)) throw new Error('Invalid saved receipt');
            const number = data.receipt_number || ''; saved.current.set(seq, number);
            setRows(previous => previous.map(row => row.order_seq === seq ? { ...row, ...data } : row));
            // A later edit must survive an earlier request finishing.
            setNumbers(previous => previous[seq]?.trim() === value ? { ...previous, [seq]: number } : previous);
          } catch (cause) {
            if (generation !== revision.current) return false;
            if (!pending.current.has(seq)) failed.current.set(seq, value);
            setError(message(cause));
          } finally { inFlight.current = null; if (generation === revision.current) repaint(); }
        }
        if (generation === revision.current && !failed.current.size) setError('');
        return failed.current.size === 0;
      } finally { running.current = null; if (generation === revision.current) { setSaving(null); repaint(); } }
    })();
    running.current = work; return work;
  }, []);

  const editNumber = (seq: number, value: string) => {
    drafts.current.set(seq, value); setNumbers(previous => ({ ...previous, [seq]: value })); failed.current.delete(seq); if (!failed.current.size) setError('');
    if (value.trim() !== (saved.current.get(seq) || '') || inFlight.current === seq) pending.current.set(seq, value.trim());
    else pending.current.delete(seq);
    repaint(); if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { void flush(); }, 650);
  };
  const retry = (seq: number) => { pending.current.set(seq, (drafts.current.get(seq) || '').trim()); failed.current.delete(seq); repaint(); void flush(); };
  closeRequest.current = () => {
    if (!onClose || closing.current) return;
    closing.current = true;
    void flush().then(ok => { if (ok) onClose(); else closing.current = false; });
  };
  useEffect(() => {
    if (!onClose) return;
    const previous = document.activeElement as HTMLElement | null, overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden'; closeButton.current?.focus();
    const keydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); closeRequest.current(); }
      if (event.key !== 'Tab') return;
      const elements = Array.from(dialog.current?.querySelectorAll<HTMLElement>('button:not(:disabled),input:not(:disabled),select:not(:disabled),[tabindex="0"]') || []);
      const first = elements[0], last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    document.addEventListener('keydown', keydown);
    return () => { document.body.style.overflow = overflow; document.removeEventListener('keydown', keydown); previous?.focus(); };
  }, [onClose]);
  const load = useCallback(async () => {
    const current = ++revision.current; setLoading(true); setError('');
    try {
      const { data } = await api.get(`${SERVER_ADDRESS}orders/bank-cash-receipts`, { params: { live_seq: liveSeq, current_page: page, per_page: pageSize, ...(search && { search }) } });
      if (current !== revision.current) return;
      if (!Array.isArray(data?.items) || !Number.isSafeInteger(data.total) || data.total < 0 || !Number.isSafeInteger(data.current_page) || data.current_page < 1) throw new Error('Invalid receipt list');
      setRows(data.items); setTotal(data.total); setPage(data.current_page);
      const entries = data.items.map((row: Receipt) => [row.order_seq, row.receipt_number || ''] as [number, string]);
      saved.current = new Map(entries); drafts.current = new Map(entries); setNumbers(Object.fromEntries(entries));
    } catch { if (current === revision.current) { setRows([]); setTotal(0); setError('현금영수증 목록을 불러오지 못했습니다. 다시 불러와 주세요.'); } }
    finally { if (current === revision.current) setLoading(false); }
  }, [liveSeq, page, pageSize, search]);
  useEffect(() => { void load(); return () => { revision.current += 1; }; }, [load]);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  const content = <Section aria-label="무통장 현금영수증 목록">
    <Header data-admin-modal-header={onClose ? "" : undefined}><h2 id="bank-cash-receipt-title">무통장 현금영수증</h2><Actions>
      <AdminActionButton disabled={loading || unsaved} onClick={() => void load()}>다시 불러오기</AdminActionButton>
      {onClose && <ModalCloseButton tone="admin" ref={closeButton} aria-label="무통장 현금영수증 닫기" onClick={() => closeRequest.current()} />}
    </Actions></Header>
    <Intro>셀러가 발급한 현금영수증의 발행 여부와 번호를 기록합니다.</Intro>
    <SearchForm role="search" aria-label="현금영수증 검색" onSubmit={event => { event.preventDefault(); if (unsaved) return; const value = searchDraft.trim(); setPage(1); if (value === search && page === 1) void load(); else setSearch(value); }}>
      <label className="search-field"><span>검색</span><input aria-label="현금영수증 검색어" maxLength={100} placeholder="주문번호, 고객명, 닉네임, 발행 번호" value={searchDraft} onChange={event => setSearchDraft(event.target.value)} /></label>
      <AdminActionButton type="submit" disabled={unsaved || loading}>검색</AdminActionButton>
      {search && <AdminActionButton type="button" disabled={unsaved} onClick={() => { setSearchDraft(''); setSearch(''); setPage(1); }}>초기화</AdminActionButton>}
      <label>표시 개수<select aria-label="현금영수증 표시 개수" value={pageSize} disabled={unsaved || loading} onChange={event => { setPageSize(Number(event.target.value)); setPage(1); }}>
        {[20,50,100].map(size => <option key={size} value={size}>{size}개</option>)}
      </select></label>
    </SearchForm>
    {error && <p role="alert">{error}</p>}
    {loading ? <p role="status">목록을 불러오는 중입니다.</p> : <Scroll data-admin-modal-body={onClose ? "table" : undefined} role="region" aria-label="현금영수증 결과" tabIndex={0}><table aria-label="현금영수증 주문 목록">
      <thead><tr><th>주문번호</th><th>고객</th><th>입금액</th><th>발행 여부</th><th>발행 번호</th><th>발행 기록일</th><th>저장 상태</th></tr></thead>
      <tbody>{rows.map(row => <tr key={row.order_seq}>
        <td>{row.order_seq}</td><td>{row.order_nickname || row.order_name}</td><td>{Number(row.paid_amount).toLocaleString('ko-KR')}원</td>
        <td><ReceiptStatus data-issued={!!row.issued}>{row.issued ? '발행' : '미발행'}</ReceiptStatus></td>
        <td><input aria-label={`주문 ${row.order_seq} 발행 번호`} inputMode="numeric" maxLength={80} value={numbers[row.order_seq] || ''}
          onChange={event => editNumber(row.order_seq, event.target.value)} onBlur={() => void flush()}
          onKeyDown={event => { if (event.key === 'Enter') { event.preventDefault(); void flush(); } }} placeholder="발행 번호" /></td>
        <td>{row.issued_at ? new Date(row.issued_at).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul', hour12: false }) : '—'}</td>
        <td aria-live="polite">{failed.current.has(row.order_seq) ? <AdminActionButton onClick={() => retry(row.order_seq)} aria-label={`주문 ${row.order_seq} 다시 저장`}>다시 저장</AdminActionButton>
          : saving === row.order_seq ? '저장 중…' : pending.current.has(row.order_seq) ? '저장 대기' : '저장됨'}</td>
      </tr>)}</tbody>
    </table>{!rows.length && <p role="status">{search ? '검색 조건에 맞는 현금영수증 내역이 없습니다.' : '입금이 확인된 무통장 주문이 없습니다.'}</p>}</Scroll>}
    <Footer data-admin-modal-footer={onClose ? "pagination" : undefined}><span>총 {total.toLocaleString('ko-KR')}건</span>
      <nav aria-label="현금영수증 페이지"><AdminActionButton disabled={loading || unsaved || page <= 1} onClick={() => setPage(value => value - 1)}>이전</AdminActionButton>
        <span aria-live="polite">{page} / {Math.max(1, Math.ceil(total / pageSize))}</span>
        <AdminActionButton disabled={loading || unsaved || page >= Math.ceil(total / pageSize)} onClick={() => setPage(value => value + 1)}>다음</AdminActionButton></nav>
    </Footer>
  </Section>;
  return onClose ? createPortal(<Overlay data-admin-modal-overlay="" onClick={event => { if (event.target === event.currentTarget) closeRequest.current(); }}>
    <Dialog data-admin-modal="" ref={dialog} role="dialog" aria-modal="true" aria-labelledby="bank-cash-receipt-title">{content}</Dialog></Overlay>, document.body) : content;
}
const Overlay = styled.div`
  position: fixed; inset: 0; z-index: var(--z-modal, 1000); display: flex; align-items: center;
  justify-content: center; padding: var(--space-4); background: var(--color-bg-overlay);
`;
const Dialog = styled.div`
  box-sizing: border-box; width: min(100%, 1100px); max-height: calc(100dvh - var(--space-8)); display: flex; flex-direction: column; overflow: hidden;
  overscroll-behavior: contain; padding: 0; border-radius: var(--radius-lg); background: var(--color-bg-elevated);
`;
const Actions = styled.div`display: flex; align-items: center; gap: var(--space-3);`;
const Section = styled.section`
  display: flex; flex-direction: column; min-height: 0; width: 100%; margin: 0; color: var(--color-text-primary); font-family: var(--font-family-base); font-size: var(--text-body);
  > p { padding-inline: var(--space-5); }
  p { color: var(--color-text-secondary); margin-block: var(--space-3); }
  p[role="alert"] { color: var(--color-text-danger); }
`;
const Header = styled.div`
  display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: var(--space-3);
  flex: 0 0 auto; padding: var(--space-4) var(--space-5); border-bottom: 1px solid var(--color-border-default);
  h2 { font-size: var(--text-body-lg); line-height: var(--leading-body); margin: 0; }
`;
const Scroll = styled.div`
  flex: 1; min-height: 0; overflow: auto; overscroll-behavior: contain;
  table { min-width: 54rem; }
  table { width: 100%; border-collapse: collapse; text-align: left; font-variant-numeric: tabular-nums; }
  th, td { padding: var(--space-3); border-bottom: 1px solid var(--color-border-default); white-space: nowrap; }
  th { position: sticky; top: 0; z-index: 1; background: var(--color-bg-muted); color: var(--color-text-secondary); font-weight: var(--font-weight-medium); }
  tbody tr:hover { background: var(--color-bg-subtle); }
  p { padding: var(--space-5); }
  label { display: inline-flex; min-height: var(--control-lg); gap: var(--space-2); align-items: center; cursor: pointer; }
  input { font: inherit; color: inherit; accent-color: var(--color-brand); }
  input[type="text"], input:not([type]) { height: var(--control-lg); box-sizing: border-box; padding-inline: var(--space-3);
    border: 1px solid var(--color-border-default); border-radius: var(--radius-md); background: var(--color-bg-surface); }
  input:focus-visible { outline: 2px solid var(--color-focus-ring); outline-offset: 2px; }
`;

const Intro = styled.p`flex: 0 0 auto; margin: 0; padding: 0 var(--space-5); line-height: var(--leading-body);`;
const SearchForm = styled.form`
  display: flex; flex: 0 0 auto; flex-wrap: wrap; align-items: flex-end; gap: var(--space-2);
  padding: 0 var(--space-5) var(--space-4); border-bottom: 1px solid var(--color-border-default);
  label { display: flex; flex-direction: column; gap: var(--space-2); color: var(--color-text-secondary); font-size: var(--text-caption); }
  .search-field { flex: 1; min-width: 12rem; }
  input, select { box-sizing: border-box; height: var(--control-lg); min-width: 0; padding: 0 var(--space-3); border: 1px solid var(--color-border-default); border-radius: var(--radius-md); background: var(--color-bg-surface); color: var(--color-text-primary); font: inherit; font-size: var(--text-body); }
  input::placeholder { color: var(--color-text-disabled); }
  button[type="submit"] { background: var(--color-brand); color: var(--color-text-on-brand); border-color: var(--color-brand); }
  :focus-visible { outline: 2px solid var(--color-focus-ring); outline-offset: 2px; }
`;
const Footer = styled.footer`
  display: flex; flex: 0 0 auto; justify-content: space-between; align-items: center; gap: var(--space-3); flex-wrap: wrap;
  padding: var(--space-3) var(--space-5); border-top: 1px solid var(--color-border-default); background: var(--color-bg-surface);
  color: var(--color-text-secondary); font-size: var(--text-body); font-variant-numeric: tabular-nums;
  nav { display: flex; align-items: center; gap: var(--space-3); }
`;

const ReceiptStatus = styled.span`
  display: inline-flex; align-items: center; min-height: var(--control-sm); padding: 0 var(--space-2); border-radius: var(--radius-sm);
  color: var(--color-text-secondary); background: var(--color-bg-muted); font-size: var(--text-caption);
  &[data-issued="true"] { color: var(--color-success); background: var(--color-success-subtle); }
`;
